import { useCallback, useEffect, useState } from 'react';

import { Error } from '../api/setup/getApiError';
import { GetFunction, ICurrentPage, IGetParams, IReturnedValues, ISortStatuses } from './types';

export function useTableData<T>(
  getFunction: GetFunction<T>,
  perPage: number = 5,
  defaultParams: IGetParams = {}
): IReturnedValues<T> {
  const [data, setData] = useState<T>();
  const [fetching, setFetching] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [currentPage, setCurrentPage] = useState<ICurrentPage>({
    offset: 0,
    current: 1
  });
  const [otherParams, setOtherParams] = useState<IGetParams>({ user_groups: '' });
  const [sortStatuses, setSortStatuses] = useState<ISortStatuses>({
    order_dir: 'asc',
    ...defaultParams
  });

  const setPage = (page: number) => {
    setCurrentPage({
      offset: (page - 1) * perPage,
      current: page
    });
  };

  const sortData = useCallback(
    (sortedItem: string) => {
      if (sortStatuses.order_column === sortedItem) {
        setSortStatuses((prev) => ({
          order_column: sortedItem,
          order_dir: prev.order_dir === 'asc' ? 'desc' : 'asc'
        }));
        return;
      }
      setSortStatuses(() => ({ order_column: sortedItem, order_dir: 'asc' }));
    },
    [sortStatuses]
  );

  const fetchData = useCallback(async () => {
    try {
      setFetching(true);
      const response: T = await getFunction({
        ...sortStatuses,
        ...otherParams,
        offset: currentPage.offset
      });
      setData(response);
    } catch (err) {
      setError(err);
    } finally {
      setFetching(false);
    }
  }, [sortStatuses, currentPage, otherParams]);

  useEffect(() => {
    fetchData();
  }, [sortStatuses, currentPage, otherParams]);

  return {
    data,
    setData,
    error,
    fetching,
    sortStatuses,
    setPage,
    page: currentPage.current,
    otherParams,
    setParams: setOtherParams,
    sortData
  };
}
